import { useEffect, useRef, useState } from 'react';
import styles from './DropdownLogo.module.css';
import { setLanguage, getCurrentLanguage, translations } from '../../translations';
import { availableLanguages, type LanguageCode } from '../../constants/languages';
import { useTranslationUpdate } from '../../hooks/useTranslationUpdate';

interface DropdownLogoProps {
  logoRef: React.RefObject<HTMLImageElement | null>;
  isClosing: boolean;
  onClose: () => void;
  onLanguageChange: () => void;
}

export default function DropdownLogo({ logoRef, isClosing, onClose, onLanguageChange }: DropdownLogoProps) {
  useTranslationUpdate();
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedLogo = logoRef.current?.contains(target);
      const clickedDropdown = dropdownRef.current?.contains(target);
      
      if (!clickedLogo && !clickedDropdown) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, logoRef]);

  const handleLanguageChange = (lang: LanguageCode) => {
    setLanguage(lang);
    setCurrentLang(lang);
    onLanguageChange();
  };

  return (
    <div ref={dropdownRef} className={`${styles.dropdown} ${isClosing ? styles.closing : ''}`}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{translations('app.language_section')}</div>
        
        {availableLanguages.map((language) => (
          <div 
            key={language.code}
            className={styles.item}
            onClick={() => handleLanguageChange(language.code)}
          >
            <div className={styles.itemLabel}>
              <span className={styles.flag}>{language.flag}</span>
              <span>{language.name}</span>
            </div>
            {currentLang === language.code && <span className={styles.checkmark}>✓</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
